(() => {
  'use strict';
  if (window.aimiiHomeMounted) return;
  window.aimiiHomeMounted = true;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const listen = (target, event, fn, signal, options = {}) => target.addEventListener(event, fn, { ...options, signal });
  const settingNumber = (value, fallback, minimum, maximum) => { const number = Number(value); return Math.min(maximum, Math.max(minimum, value !== undefined && value !== '' && Number.isFinite(number) ? number : fallback)); };
  const normalClick = event => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  function mount(selector, init, scope) {
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      const controller = new AbortController();
      const destroy = init(root, controller.signal) || (() => {});
      instances.set(root, () => { controller.abort(); destroy(); instances.delete(root); });
    });
  }
  function categories(root, signal) {
    const hintEnabled = root.dataset.hintEnabled !== 'false', autoReveal = root.dataset.autoReveal !== 'false';
    const hintWait = settingNumber(root.dataset.hintWait, 4000, 1000, 9000), hintDuration = settingNumber(root.dataset.hintDuration, 700, 200, 2000), hintDistance = settingNumber(root.dataset.hintDistance, 60, 20, 120);
    const track = root.querySelector('[data-category-track]');
    const results = root.querySelector('[data-aimii-results]');
    const title = root.querySelector('[data-collection-title]');
    const count = root.querySelector('[data-result-count]');
    const head = root.querySelector('.collection-head');
    const controls = root.querySelector('.menu-controls');
    const input = root.querySelector('[data-product-search]');
    const toggle = root.querySelector('[data-search-toggle]');
    const close = root.querySelector('[data-search-close]');
    const categoryCache = new Map(), categoryRequests = new Map();
    const cacheLifetime = 60000, cacheLimit = 16;
    let viewVersion = 0, foregroundPending = false, prefetchTimer = 0, prefetchIdle = null, prefetchRunning = false;
    let loaded = document.readyState === 'complete';
    let hintTimer = 0, hintFrame = 0, hintVisible = false, hintUsed = false, resize, intersection;
    try { hintUsed = sessionStorage.getItem('aimii-category-hint') === 'used'; } catch (_) {}
    let currentCategory = root.querySelector('[data-category-link].active');
    const preferences = active => ({
      limit: settingNumber(active?.dataset.productsPerPage ?? root.dataset.productsPerPage, 24, 0, 250),
      availableFirst: (active?.dataset.availableFirst ?? root.dataset.availableFirst) === 'true'
    });
    let currentPreferences = preferences(currentCategory), visibleLimit = currentPreferences.limit || Infinity;
    let searchMode = Boolean(root.dataset.searchTerms), searchReady = searchMode, searchTimer = 0, searchSnapshot = null;
    const cards = () => [...results.querySelectorAll('[data-aimii-card]')];
    const notify = () => document.dispatchEvent(new CustomEvent('aimii:products:rendered', {detail: {root: results}}));
    let selectionFrame = 0, selectionLayout = null;
    const cancelSelectionReturn = () => {
      cancelAnimationFrame(selectionFrame); selectionFrame = 0;
      if (!selectionLayout) return;
      for (const [property, value, priority] of selectionLayout) {
        if (value) results.style.setProperty(property, value, priority);
        else results.style.removeProperty(property);
      }
      selectionLayout = null;
    };
    const returnToSelection = () => {
      const inset = controls ? Math.max(0, parseFloat(getComputedStyle(controls).top) || 0) : 0;
      const barBottom = inset + (controls?.getBoundingClientRect().height || 0);
      // A sticky bar's current rect is not its natural position in the document.
      const target = Math.max(0, root.getBoundingClientRect().top + window.scrollY - inset);
      const from = window.scrollY;
      if (from <= target + 8 || (!selectionLayout && (head || results).getBoundingClientRect().top >= barBottom - 1)) {
        cancelSelectionReturn(); return;
      }
      cancelAnimationFrame(selectionFrame); selectionFrame = 0;
      if (motion.matches) { cancelSelectionReturn(); window.scrollTo({top: target, behavior: 'instant'}); return; }
      if (!selectionLayout) {
        selectionLayout = ['min-height', 'overflow-anchor'].map(property => [property, results.style.getPropertyValue(property), results.style.getPropertyPriority(property)]);
        // Keep a cached short list from clamping/anchoring the document mid-scroll.
        results.style.setProperty('min-height', `${results.getBoundingClientRect().height}px`);
        results.style.setProperty('overflow-anchor', 'none');
      }
      let began = null;
      const step = time => {
        if (signal.aborted || !root.isConnected) { cancelSelectionReturn(); return; }
        if (began === null) began = time;
        const phase = Math.min(1, (time - began) / 300), ease = 1 - (1 - phase) ** 3;
        window.scrollTo({top: from + (target - from) * ease, behavior: 'instant'});
        if (phase < 1) selectionFrame = requestAnimationFrame(step);
        else cancelSelectionReturn();
      };
      selectionFrame = requestAnimationFrame(step);
    };
    const interruptSelectionReturn = event => {
      if (!event.target.closest?.('[data-category-link]')) cancelSelectionReturn();
    };
    listen(window, 'wheel', cancelSelectionReturn, signal, {passive: true});
    listen(window, 'pointerdown', interruptSelectionReturn, signal, {passive: true});
    listen(window, 'touchstart', interruptSelectionReturn, signal, {passive: true});
    listen(window, 'touchmove', cancelSelectionReturn, signal, {passive: true});
    listen(window, 'keydown', event => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab'].includes(event.key)) cancelSelectionReturn();
    }, signal);
    listen(document, 'aimii:product-opening', cancelSelectionReturn, signal);
    listen(document, 'visibilitychange', () => { if (document.hidden) cancelSelectionReturn(); }, signal);
    listen(motion, 'change', cancelSelectionReturn, signal);

    const filter = () => {
      for (const [setting, selector] of [['cardShowDescription', '[data-aimii-card-description]'], ['cardShowWeight', '[data-aimii-card-weight]'], ['cardShowBadges', '[data-aimii-card-badge]'], ['cardShowComparePrice', '[data-aimii-card-compare-price]']]) {
        if (root.dataset[setting] !== undefined) results.querySelectorAll(selector).forEach(element => { element.hidden = root.dataset[setting] === 'false'; });
      }
      const items = cards(), grid = results.querySelector('[data-product-grid]');
      items.forEach((card, index) => { card.hidden = index >= visibleLimit; });
      const found = Math.min(items.length, visibleLimit);
      const kind = searchMode ? 'products' : currentCategory?.dataset.kind || root.dataset.resultKind || 'products';
      count.textContent = searchMode && !searchReady ? 'Suche im gesamten Sortiment …' : `${found} ${kind === 'bundles' ? (found === 1 ? 'Paket' : 'Pakete') : (found === 1 ? 'Gericht' : 'Gerichte')} · von uns selbst gemacht`;
      results.querySelector('[data-empty-results]').hidden = found !== 0 || (searchMode && !searchReady);
      grid?.setAttribute('data-group', kind === 'bundles' ? 'bundles' : '');
      if (grid) grid.hidden = searchMode && !searchReady;
      let more = results.querySelector('[data-products-more]');
      if (items.length > visibleLimit && !more) {
        more = root.querySelector('[data-products-more-template]')?.content.firstElementChild.cloneNode(true) || document.createElement('button');
        more.type = 'button'; more.className = 'text-link'; more.dataset.productsMore = ''; if (!more.textContent) more.textContent = 'Mehr anzeigen'; results.append(more);
      }
      if (more) more.hidden = items.length <= visibleLimit || (searchMode && !searchReady);
      const next = results.querySelector('[data-products-next]');
      if (next) next.hidden = items.length > visibleLimit || (searchMode && !searchReady);
    };
    const cancelSearch = () => {
      clearTimeout(searchTimer); searchTimer = 0;
      categoryRequests.forEach((entry, key) => { if (entry.search) { entry.controller.abort(); categoryRequests.delete(key); } });
    };
    const restoreSearch = (fallback = true) => {
      const hadSearch = searchMode, hadSnapshot = Boolean(searchSnapshot);
      cancelSearch(); viewVersion++; searchMode = false; searchReady = false; foregroundPending = false;
      results.removeAttribute('aria-busy'); root.querySelector('[data-categories-error]').hidden = true;
      if (searchSnapshot) {
        results.innerHTML = searchSnapshot.markup; title.textContent = searchSnapshot.title;
        currentCategory = searchSnapshot.category; currentPreferences = searchSnapshot.preferences; visibleLimit = searchSnapshot.visibleLimit;
        root.querySelectorAll('[data-category-link]').forEach(link => { link.classList.toggle('active', link === currentCategory); link.removeAttribute('aria-current'); });
        currentCategory?.setAttribute('aria-current', 'true'); searchSnapshot = null;
      }
      filter(); notify(); schedulePrefetch();
      if (hadSearch && !hadSnapshot && fallback) {
        const url = root.dataset.allProductsUrl || '/collections/all';
        const all = [...root.querySelectorAll('[data-category-link]')].find(link => new URL(link.href).pathname === new URL(url, location.origin).pathname);
        load(url, false, all || null);
      }
    };
    const searchOpen = open => {
      head.classList.toggle('search-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      input.tabIndex = open ? 0 : -1; close.tabIndex = open ? 0 : -1;
      input.setAttribute('aria-hidden', String(!open)); close.setAttribute('aria-hidden', String(!open));
      if (open) input.focus(); else { input.value = ''; restoreSearch(); toggle.focus(); }
    };
    const searchProducts = () => {
      const query = input.value.trim();
      if (!query) { restoreSearch(); return; }
      if (!searchMode) searchSnapshot = {markup: results.innerHTML, title: title.textContent, category: currentCategory, preferences: currentPreferences, visibleLimit};
      cancelSearch(); cancelPrefetch(); viewVersion++; searchMode = true; searchReady = false; foregroundPending = true;
      currentPreferences = {limit: settingNumber(root.dataset.defaultProductsPerPage, 24, 6, 48), availableFirst: false}; visibleLimit = currentPreferences.limit;
      root.querySelectorAll('[data-category-link]').forEach(link => { link.classList.remove('active'); link.removeAttribute('aria-current'); });
      title.textContent = 'Suchergebnisse'; results.setAttribute('aria-busy', 'true'); root.querySelector('[data-categories-error]').hidden = true; filter();
      searchTimer = setTimeout(() => {
        searchTimer = 0;
        const url = new URL(root.dataset.searchUrl || '/search', location.origin);
        url.searchParams.set('q', query); url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        load(url.href);
      }, 250);
    };
    listen(toggle, 'click', () => searchOpen(true), signal);
    listen(close, 'click', () => searchOpen(false), signal);
    listen(input, 'input', searchProducts, signal);
    listen(input, 'keydown', event => { if (event.key === 'Escape') searchOpen(false); }, signal);
    const overflow = () => track && track.scrollWidth > track.clientWidth + 3;
    const edges = () => track?.closest('.category-strip').classList.toggle('has-overflow', overflow());
    const clearHint = () => { clearTimeout(hintTimer); hintTimer = 0; cancelAnimationFrame(hintFrame); hintFrame = 0; track?.closest('.category-strip').classList.remove('is-hinting'); };
    const stopHint = () => { hintUsed = true; try { sessionStorage.setItem('aimii-category-hint', 'used'); } catch (_) {} clearHint(); edges(); };
    const scheduleHint = () => {
      clearTimeout(hintTimer);
      if (!hintEnabled || !track || !hintVisible || hintUsed || hintFrame || document.hidden || motion.matches || !overflow()) return;
      hintTimer = setTimeout(() => {
        hintTimer = 0;
        if (!hintVisible || hintUsed || document.hidden || motion.matches || !overflow()) return;
        const start = track.scrollLeft, distance = Math.min(hintDistance, track.scrollWidth - track.clientWidth - start);
        let started = null;
        track.closest('.category-strip').classList.add('is-hinting');
        const step = time => {
          if (!hintVisible || hintUsed || document.hidden || motion.matches) { clearHint(); return; }
          if (started === null) started = time;
          const phase = Math.min(1, (time - started) / hintDuration);
          track.scrollLeft = start + Math.sin(Math.PI * phase) * distance;
          if (phase < 1) hintFrame = requestAnimationFrame(step);
          else { track.scrollLeft = start; hintFrame = 0; track.closest('.category-strip').classList.remove('is-hinting'); edges(); scheduleHint(); }
        };
        hintFrame = requestAnimationFrame(step);
      }, hintWait);
    };
    const reveal = link => {
      if (!autoReveal || !overflow()) return;
      const inset = Math.min(128, Math.max(64, track.clientWidth * .36));
      const left = link.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - inset;
      track.scrollTo({ left: Math.max(0, Math.min(track.scrollWidth - track.clientWidth, left)), behavior: motion.matches ? 'instant' : 'smooth' });
    };
    const categoryUrl = (url, active = null) => {
      const nativeUrl = new URL(url, location.origin);
      if (nativeUrl.origin !== location.origin) throw new Error('Invalid category URL');
      nativeUrl.hash = '';
      nativeUrl.searchParams.set('section_id', active?.dataset.featured === 'true' ? root.dataset.sectionId || root.dataset.fragmentSection : root.dataset.fragmentSection);
      return nativeUrl;
    };
    const rememberCategory = (key, entry) => {
      categoryCache.delete(key);
      categoryCache.set(key, entry);
      while (categoryCache.size > cacheLimit) categoryCache.delete(categoryCache.keys().next().value);
    };
    const initialCategory = root.querySelector('[data-category-link].active');
    if (initialCategory && (initialCategory.dataset.featured === 'true' || categoryUrl(initialCategory.href).href === categoryUrl(location.href).href)) {
      rememberCategory(categoryUrl(initialCategory.href, initialCategory).href, {markup: results.innerHTML, title: title.textContent, time: Date.now()});
    }
    const fetchCategory = (nativeUrl, background = false) => {
      const key = nativeUrl.href;
      if (categoryRequests.has(key)) { const existing = categoryRequests.get(key); if (!background) existing.background = false; return existing.promise; }
      if (!background) document.dispatchEvent(new CustomEvent('aimii:catalog-request'));
      const controller = new AbortController();
      root.dataset.aimiiCatalogBusy = '';
      const entry = {controller, background, promise: null, search: nativeUrl.pathname === new URL(root.dataset.searchUrl || '/search', location.origin).pathname};
      entry.promise = (async () => {
        try {
          const response = await fetch(nativeUrl, {signal: controller.signal, credentials: 'same-origin', ...(background ? {priority: 'low'} : {})});
          if (!response.ok) throw new Error('Could not load products');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = html.querySelector('[data-aimii-results]');
          if (!next?.querySelector('[data-product-grid]') || !next.querySelector('[data-empty-results]')) throw new Error('Invalid category response');
          if (controller.signal.aborted || signal.aborted) throw new DOMException('Category lifecycle ended', 'AbortError');
          next.querySelectorAll('script').forEach(script => script.remove());
          const saved = {markup: next.innerHTML, title: html.querySelector('[data-collection-title]')?.textContent, time: Date.now()};
          rememberCategory(key, saved);
          return saved;
        } finally { if (categoryRequests.get(key) === entry) categoryRequests.delete(key); if (!categoryRequests.size) delete root.dataset.aimiiCatalogBusy; }
      })();
      categoryRequests.set(key, entry);
      return entry.promise;
    };
    const completeCategory = async (saved, background = false) => {
      const template = document.createElement('template'); template.innerHTML = saved.markup;
      const contents = template.content, grid = contents.querySelector('[data-product-grid]'), visited = new Set();
      let next = contents.querySelector('[data-products-next]');
      while (next) {
        const url = categoryUrl(next.href || next.getAttribute('href'));
        if (visited.has(url.href)) throw new Error('Repeated product page');
        visited.add(url.href);
        const page = await fetchCategory(url, background);
        if (signal.aborted) throw new DOMException('Category lifecycle ended', 'AbortError');
        const fragment = document.createElement('template'); fragment.innerHTML = page.markup;
        grid.append(...fragment.content.querySelector('[data-product-grid]').children);
        next.remove(); next = fragment.content.querySelector('[data-products-next]');
        if (next) contents.append(next);
      }
      return {...saved, markup: template.innerHTML};
    };
    const orderedMarkup = (saved, availableFirst) => {
      if (!availableFirst) return saved;
      const template = document.createElement('template'); template.innerHTML = saved.markup;
      const grid = template.content.querySelector('[data-product-grid]');
      const items = [...grid.children];
      grid.append(...items.filter(card => card.dataset.productAvailable !== 'false'), ...items.filter(card => card.dataset.productAvailable === 'false'));
      return {...saved, markup: template.innerHTML};
    };
    const renderCategory = (saved, append, active) => {
      const template = document.createElement('template');
      template.innerHTML = saved.markup;
      const next = template.content;
      if (append) {
        visibleLimit += currentPreferences.limit || Infinity;
        results.querySelector('[data-product-grid]').append(...next.querySelector('[data-product-grid]').children);
        results.querySelector('[data-products-next]')?.remove();
        const nextLink = next.querySelector('[data-products-next]');
        if (nextLink) results.append(nextLink);
      } else {
        results.replaceChildren(...next.children);
        title.textContent = searchMode ? saved.title || 'Suchergebnisse' : active?.dataset.title || saved.title || title.textContent;
        if (active) { currentCategory = active; currentPreferences = preferences(active); }
        visibleLimit = currentPreferences.limit || Infinity;
        if (active) { root.querySelectorAll('[data-category-link]').forEach(link => { link.classList.toggle('active', link === active); link.removeAttribute('aria-current'); }); active.setAttribute('aria-current', 'true'); }
      }
      if (searchMode) searchReady = true;
      filter(); notify();
    };
    const prefetchQueue = [...new Map([...root.querySelectorAll('[data-category-link]')].filter(link => link.dataset.featured !== 'true').map(link => [categoryUrl(link.href).href, link])).values()].slice(0, cacheLimit);
    const cancelPrefetch = () => {
      clearTimeout(prefetchTimer); prefetchTimer = 0;
      if (prefetchIdle !== null) window.cancelIdleCallback(prefetchIdle);
      prefetchIdle = null;
    };
    listen(document, 'aimii:product-opening', () => { cancelPrefetch(); categoryRequests.forEach(entry => { if (entry.background) entry.controller.abort(); }); }, signal);
    listen(document, 'aimii:product-closed', () => schedulePrefetch(), signal);
    const schedulePrefetch = () => {
      if (!loaded || signal.aborted || document.hidden || document.querySelector('#product-dialog[open]') || foregroundPending || prefetchRunning || prefetchIdle !== null || prefetchTimer || !prefetchQueue.length) return;
      if (window.AimiiProducts?.prefetching) { prefetchTimer = setTimeout(() => { prefetchTimer = 0; schedulePrefetch(); }, 250); return; }
      const run = async () => {
        prefetchIdle = null; prefetchTimer = 0;
        if (signal.aborted || document.hidden || document.querySelector('#product-dialog[open]') || foregroundPending) return;
        if (window.AimiiProducts?.prefetching) { schedulePrefetch(); return; }
        prefetchRunning = true;
        const link = prefetchQueue.shift(), url = categoryUrl(link.href), saved = categoryCache.get(url.href);
        try {
          if (!saved || Date.now() - saved.time >= cacheLifetime) {
            let fresh = await fetchCategory(url, true); const prefs = preferences(link);
            if (prefs.availableFirst || prefs.limit === 0) fresh = {...await completeCategory(fresh, true), complete: true, time: Date.now()};
            rememberCategory(url.href, fresh);
          }
        } catch (_) {}
        finally { prefetchRunning = false; schedulePrefetch(); }
      };
      if (window.requestIdleCallback && window.cancelIdleCallback) prefetchIdle = window.requestIdleCallback(run, {timeout: 2000});
      else prefetchTimer = setTimeout(run, 200);
    };
    const load = async (url, append = false, active = null) => {
      const version = ++viewVersion;
      cancelPrefetch();
      const nativeUrl = categoryUrl(url, active);
      let saved = categoryCache.get(nativeUrl.href);
      const prefs = searchMode ? currentPreferences : active ? preferences(active) : currentPreferences;
      const needsComplete = !searchMode && (prefs.availableFirst || prefs.limit === 0);
      if (needsComplete && !saved?.complete && saved?.markup.includes('data-products-next')) saved = null;
      const current = () => !signal.aborted && root.isConnected && version === viewVersion;
      root.querySelector('[data-categories-error]').hidden = true;
      results.removeAttribute('aria-busy');
      if (saved) {
        rememberCategory(nativeUrl.href, saved);
        renderCategory(orderedMarkup(saved, prefs.availableFirst), append, active);
        if (Date.now() - saved.time < cacheLifetime) { foregroundPending = false; schedulePrefetch(); return; }
      } else results.setAttribute('aria-busy', 'true');
      foregroundPending = !saved;
      try {
        let fresh = await fetchCategory(nativeUrl, !!saved);
        if (needsComplete) { fresh = {...await completeCategory(fresh, !!saved), complete: true, time: Date.now()}; rememberCategory(nativeUrl.href, fresh); }
        if (current() && (!saved || (!append && !results.contains(document.activeElement)))) renderCategory(orderedMarkup(fresh, prefs.availableFirst), append, active);
      } catch (error) {
        if (!saved && error.name !== 'AbortError' && current()) { root.querySelector('[data-categories-error]').hidden = false; root.querySelector('[data-categories-retry]').href = url; }
      } finally {
        if (current()) { foregroundPending = false; results.removeAttribute('aria-busy'); }
        schedulePrefetch();
      }
    };
    listen(root, 'click', event => {
      const category = event.target.closest('[data-category-link]');
      const next = event.target.closest('[data-products-next]');
      const more = event.target.closest('[data-products-more]');
      if (more && normalClick(event)) { event.preventDefault(); cancelSelectionReturn(); viewVersion++; visibleLimit += currentPreferences.limit || cards().length; filter(); notify(); return; }
      if ((!category && !next) || !normalClick(event)) return;
      if (category?.dataset.featured === 'true' && root.dataset.featuredRestorable === 'false') return;
      event.preventDefault();
      if (next) { cancelSelectionReturn(); load(next.href, true); return; }
      if (category !== currentCategory || searchMode) returnToSelection();
      if (searchMode) { input.value = ''; restoreSearch(false); }
      stopHint(); reveal(category);
      load(category.href, false, category);
    }, signal);
    listen(window, 'load', () => { loaded = true; schedulePrefetch(); }, signal, {once: true});
    listen(document, 'visibilitychange', () => { if (document.hidden) cancelPrefetch(); else schedulePrefetch(); }, signal);
    schedulePrefetch();
    if (track) {
      listen(track, 'pointerdown', stopHint, signal);
      listen(track, 'wheel', stopHint, signal, { passive: true });
      listen(track, 'focusin', stopHint, signal);
      listen(track, 'scroll', () => { edges(); if (!hintFrame && track.scrollLeft > 2) stopHint(); }, signal, { passive: true });
      listen(track, 'keydown', event => { if (!['ArrowLeft','ArrowRight'].includes(event.key)) return; event.preventDefault(); stopHint(); track.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * Math.max(120, track.clientWidth * .72), behavior: motion.matches ? 'instant' : 'smooth' }); }, signal);
      intersection = new IntersectionObserver(entries => { hintVisible = entries[0].isIntersecting && entries[0].intersectionRatio >= .7; if (!hintVisible) clearHint(); scheduleHint(); }, { threshold: .7 });
      intersection.observe(track.closest('.category-strip'));
      resize = new ResizeObserver(() => { edges(); scheduleHint(); }); resize.observe(track);
      listen(document, 'visibilitychange', () => { if (document.hidden) clearHint(); scheduleHint(); }, signal);
      listen(motion, 'change', () => { clearHint(); scheduleHint(); }, signal);
      document.fonts.ready.then(() => { if (!signal.aborted) { edges(); scheduleHint(); } });
      const active = root.querySelector('[data-category-link].active'); if (active) reveal(active);
    }
    filter();
    if (!searchMode && (currentPreferences.availableFirst || currentPreferences.limit === 0) && results.querySelector('[data-products-next]')) load(location.href, false, currentCategory);
    if (searchMode) { head.classList.add('search-open'); toggle.setAttribute('aria-expanded', 'true'); input.tabIndex = 0; close.tabIndex = 0; input.setAttribute('aria-hidden', 'false'); close.setAttribute('aria-hidden', 'false'); }
    return () => {
      cancelSelectionReturn(); cancelSearch(); viewVersion++; cancelPrefetch(); prefetchQueue.length = 0;
      categoryRequests.forEach(entry => entry.controller.abort()); categoryRequests.clear(); categoryCache.clear();
      results.removeAttribute('aria-busy'); clearHint(); resize?.disconnect(); intersection?.disconnect();
    };
  }
  function accordion(root, signal, { selector = '.faq-item', editor = true } = {}) {
    const animations = new Map();
    const expand = (item, expanded) => {
      const answer = item.querySelector('.faq-answer');
      if (!answer) return;
      const from = answer.getBoundingClientRect().height;
      const opacity = item.open ? getComputedStyle(answer).opacity : '0';
      animations.get(item)?.cancel();
      item.dataset.expanded = String(expanded); item.querySelector('summary').setAttribute('aria-expanded', String(expanded)); item.open = true;
      answer.style.height = 'auto';
      const to = expanded ? answer.scrollHeight : 0;
      const finish = () => { item.open = expanded; answer.style.height = expanded ? 'auto' : '0px'; animations.delete(item); };
      if (motion.matches || !answer.animate) { finish(); return; }
      answer.style.height = `${from}px`;
      const animation = answer.animate([{ height: `${from}px`, opacity }, { height: `${to}px`, opacity: expanded ? 1 : 0 }], { duration: 320, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
      animations.set(item, animation);
      animation.onfinish = () => { if (animations.get(item) !== animation) return; finish(); animation.cancel(); };
    };
    listen(root, 'click', event => {
      const summary = event.target.closest(`${selector} > summary`); if (!summary || !root.contains(summary)) return;
      event.preventDefault(); const item = summary.parentElement, expanded = item.dataset.expanded !== 'true';
      if (expanded) root.querySelectorAll(`${selector}[data-expanded="true"]`).forEach(other => { if (other !== item) expand(other, false); });
      expand(item, expanded);
    }, signal);
    if (editor) listen(document, 'shopify:block:select', event => { if (root.contains(event.target)) { const item = event.target.closest(selector); if (item) expand(item, true); } }, signal);
    return () => {
      animations.forEach((animation, item) => {
        animation.onfinish = null; animation.cancel();
        const expanded = item.dataset.expanded === 'true';
        item.open = expanded; item.querySelector('.faq-answer').style.height = expanded ? 'auto' : '0px';
      });
      animations.clear();
    };
  }
  // Cart and product scopes reuse the same FAQ animation and delegated controls.
  const accordionScopes = new WeakMap();
  window.AimiiAccordion = {
    mount(root, { signal, selector = '.faq-item', editor = false } = {}) {
      if (accordionScopes.has(root)) return accordionScopes.get(root);
      const controller = new AbortController();
      let destroyed = false;
      const cancel = signal?.aborted ? () => {} : accordion(root, controller.signal, { selector, editor });
      const handle = {
        cancel,
        destroy() {
          if (destroyed) return;
          destroyed = true; controller.abort(); cancel();
          signal?.removeEventListener('abort', handle.destroy);
          accordionScopes.delete(root);
        }
      };
      if (signal?.aborted) { handle.destroy(); return handle; }
      accordionScopes.set(root, handle);
      signal?.addEventListener('abort', handle.destroy, { once: true });
      return handle;
    }
  };
  function reviews(root, signal) {
    const autoplay = root.dataset.autoplayEnabled !== 'false';
    const interval = settingNumber(root.dataset.autoplayInterval, 3000, 1000, 9000), duration = settingNumber(root.dataset.slideDuration, 650, 200, 2000);
    const track = root.querySelector('[data-review-track]'), dots = root.querySelector('[data-review-dots]'), dialog = root.querySelector('[data-review-dialog]');
    let reviewOpener = null;
    let timer = 0, frame = 0, target = null, progress = null, progressFill = null, deadline = 0, started = false, horizontal = false, pointer = null, manualScroll = false;
    const stops = () => { const max = Math.max(0, track.scrollWidth - track.clientWidth), gap = parseFloat(getComputedStyle(track).gap) || 14, step = (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + gap, output = []; for (let left = 0; left < max - 3; left += step) output.push(left); output.push(max); return output; };
    const index = (positions = stops()) => positions.reduce((best, value, i) => Math.abs(value - (target ?? track.scrollLeft)) < Math.abs(positions[best] - (target ?? track.scrollLeft)) ? i : best, 0);
    const stopProgress = (complete = false) => {
      if (progressFill?.isConnected) {
        const dotWidth = progressFill.parentElement.getBoundingClientRect().width;
        const fraction = complete ? 1 : dotWidth ? parseFloat(getComputedStyle(progressFill).width) / dotWidth : 0;
        // Preserve the rendered percentage before cancelling the animation's temporary width.
        progressFill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
      }
      progress?.cancel(); progress = null; progressFill = null;
    };
    const paintProgress = () => {
      stopProgress();
      const fill = dots.querySelector('.active .review-time-fill'); if (!fill) return;
      progressFill = fill;
      const remaining = Math.max(0, deadline - Date.now()), elapsed = Math.min(1, Math.max(0, 1 - remaining / interval));
      fill.style.width = motion.matches ? '100%' : `${elapsed * 100}%`;
      if (timer && started && !motion.matches && remaining > 0 && fill.animate) progress = fill.animate([{ width: `${elapsed * 100}%` }, { width: '100%' }], { duration: remaining, easing: 'linear', fill: 'forwards' });
    };
    const update = () => {
      const positions = stops(), current = index(positions);
      root.querySelector('[data-review-direction="-1"]').disabled = current === 0;
      root.querySelector('[data-review-direction="1"]').disabled = current === positions.length - 1;
      let changed = false;
      if (dots.children.length !== positions.length) { stopProgress(); dots.replaceChildren(...positions.map(() => { const dot = document.createElement('span'), fill = document.createElement('i'); fill.className = 'review-time-fill'; dot.append(fill); return dot; })); changed = true; }
      const outgoing = dots.querySelector('.active');
      if (outgoing && outgoing !== dots.children[current]) { stopProgress(); outgoing.classList.add('is-retiring'); }
      const left = Math.max(0, Math.min(positions.at(-1), track.scrollLeft)), next = positions.findIndex(position => position >= left);
      const position = next > 0 ? next - 1 + (left - positions[next - 1]) / (positions[next] - positions[next - 1]) : 0;
      [...dots.children].forEach((dot, i) => {
        const active = current === i, expansion = Math.max(0, 1 - Math.abs(i - position)), width = `${6 + 42 * expansion}px`;
        // Actual horizontal position drives both shapes, including interrupted slides and native swipes.
        dot.style.width = width; dot.style.flexBasis = width;
        if (dot.classList.contains('active') !== active) changed = true;
        dot.classList.toggle('active', active);
        if (active) dot.classList.remove('is-retiring');
        const fade = Math.min(1, expansion / .15), fill = dot.firstElementChild;
        fill.style.opacity = active ? '1' : !motion.matches && dot.classList.contains('is-retiring') ? String(fade * fade * (3 - 2 * fade)) : '0';
        if (!active && expansion === 0) { dot.classList.remove('is-retiring'); fill.style.width = '0%'; }
      });
      if (changed) paintProgress();
    };
    const cancelSlide = (keepSnapDisabled = false) => { cancelAnimationFrame(frame); frame = 0; target = null; if (!keepSnapDisabled) track.classList.remove('is-sliding'); };
    const slide = left => {
      // A new intent starts where the previous frame stopped, without a native snap between them.
      cancelSlide(true); const from = track.scrollLeft, delta = left - from;
      if (motion.matches || Math.abs(delta) < 1) { track.scrollLeft = left; track.classList.remove('is-sliding'); update(); return; }
      target = left; track.classList.add('is-sliding'); update(); let began = null;
      const step = time => { if (began === null) began = time; const phase = Math.min(1, (time - began) / duration), ease = phase < .5 ? 2 * phase ** 2 : 1 - (-2 * phase + 2) ** 2 / 2; track.scrollLeft = from + delta * ease; update(); if (phase < 1) frame = requestAnimationFrame(step); else { track.scrollLeft = left; frame = 0; target = null; track.classList.remove('is-sliding'); update(); } }; frame = requestAnimationFrame(step);
    };
    const schedule = () => {
      clearTimeout(timer); timer = 0; deadline = 0; stopProgress();
      if (!autoplay || !started || document.hidden || dialog?.open || motion.matches || stops().length < 2) { paintProgress(); return; }
      deadline = Date.now() + interval;
      const advance = () => { if (horizontal) { timer = setTimeout(advance, 100); return; } stopProgress(true); manualScroll = false; const positions = stops(); slide(positions[(index(positions) + 1) % positions.length]); schedule(); };
      timer = setTimeout(advance, interval); paintProgress();
    };
    const manual = () => { cancelSlide(); manualScroll = true; update(); schedule(); };
    const excerpts = [...track.querySelectorAll('[data-review-excerpt]')];
    const measureExcerpts = () => { if (signal.aborted) return; for (const quote of excerpts) { const button = quote.closest('[data-review-card]')?.querySelector('[data-review-read-more]'); if (button) button.hidden = quote.scrollHeight <= quote.clientHeight + 1; } };
    const unlockReview = () => { if (!document.querySelector('[data-review-dialog][open]')) document.documentElement.classList.remove('aimii-review-open'); };
    const openReview = button => {
      const card = button.closest('[data-review-card]'), content = dialog?.querySelector('[data-review-dialog-content]');
      if (!card || !content || typeof dialog.showModal !== 'function') return;
      const nodes = ['.review-stars','[data-review-excerpt]','.review-person','.review-product'].map(selector => card.querySelector(selector)?.cloneNode(true)).filter(Boolean);
      for (const node of nodes) {
        node.classList.remove('review-excerpt'); node.removeAttribute('data-review-excerpt'); node.removeAttribute('data-product-open');
        node.querySelectorAll('[id]').forEach(element => element.removeAttribute('id')); node.removeAttribute('id');
      }
      content.replaceChildren(...nodes);
      const name = card.querySelector('.review-person strong')?.textContent.trim();
      dialog.querySelector('h2').textContent = name ? `Bewertung von ${name}` : 'Bewertung';
      reviewOpener = button; button.setAttribute('aria-expanded', 'true'); cancelSlide(); horizontal = false; pointer = null;
      dialog.showModal(); document.documentElement.classList.add('aimii-review-open'); schedule();
    };
    if (dialog) {
      listen(dialog, 'click', event => { if (event.target.closest('[data-review-dialog-close]')) { dialog.close(); return; } if (event.target === dialog) { const bounds = dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close(); } }, signal);
      listen(dialog, 'close', () => { unlockReview(); const button = reviewOpener; reviewOpener = null; button?.setAttribute('aria-expanded', 'false'); if (!signal.aborted && button?.isConnected) (button.hidden ? track : button).focus({preventScroll:true}); schedule(); }, signal);
    }
    const excerptResize = new ResizeObserver(measureExcerpts); excerpts.forEach(quote => excerptResize.observe(quote)); measureExcerpts();
    document.fonts?.ready.then(measureExcerpts); if (document.fonts?.addEventListener) listen(document.fonts, 'loadingdone', measureExcerpts, signal);
    listen(root, 'click', event => { const readMore = event.target.closest('[data-review-read-more]'); if (readMore) { openReview(readMore); return; } const button = event.target.closest('[data-review-direction]'); if (!button || dialog?.open) return; manualScroll = false; const positions = stops(); slide(positions[Math.max(0, Math.min(positions.length - 1, index(positions) + Number(button.dataset.reviewDirection)))]); schedule(); }, signal);
    listen(track, 'keydown', event => { if (!['ArrowLeft','ArrowRight'].includes(event.key)) return; event.preventDefault(); manualScroll = false; const positions = stops(); slide(positions[Math.max(0, Math.min(positions.length - 1, index(positions) + (event.key === 'ArrowRight' ? 1 : -1)))]); schedule(); }, signal);
    listen(track, 'pointerdown', event => { pointer = { id: event.pointerId, x: event.clientX, y: event.clientY }; }, signal);
    listen(track, 'pointermove', event => { if (!pointer || pointer.id !== event.pointerId || horizontal) return; const dx = Math.abs(event.clientX - pointer.x), dy = Math.abs(event.clientY - pointer.y); if (dx > 5 && dx > dy) { horizontal = true; manual(); } }, signal, { passive: true });
    const finish = event => { if (!pointer || pointer.id !== event.pointerId) return; pointer = null; if (horizontal) { horizontal = false; schedule(); } };
    listen(document, 'pointerup', finish, signal); listen(document, 'pointercancel', finish, signal);
    listen(track, 'wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) manual(); }, signal, { passive: true });
    listen(track, 'scroll', () => { update(); if (!frame && !target && horizontal) schedule(); }, signal, { passive: true });
    listen(track, 'scrollend', () => { update(); if (manualScroll && !frame) { manualScroll = false; schedule(); } }, signal);
    listen(document, 'visibilitychange', schedule, signal); listen(motion, 'change', () => { cancelSlide(); update(); schedule(); }, signal);
    const observer = new IntersectionObserver(entries => { if (!started && entries[0].isIntersecting && entries[0].intersectionRatio >= .25) { started = true; schedule(); } }, { threshold: .25 }); observer.observe(root);
    let trackWidth = track.clientWidth;
    const resize = new ResizeObserver(() => { if (track.clientWidth !== trackWidth) { trackWidth = track.clientWidth; cancelSlide(); } update(); }); resize.observe(track); update();
    return () => { clearTimeout(timer); stopProgress(); cancelSlide(); observer.disconnect(); resize.disconnect(); excerptResize.disconnect(); if (dialog?.open) dialog.close(); reviewOpener?.setAttribute('aria-expanded', 'false'); reviewOpener = null; unlockReview(); };
  }
  function comparison(root, signal) {
    const stage = root.querySelector('.ramen-comparison-stage'), range = stage.querySelector('.comparison-range');
    let drag = null, hintTimer = 0, hintFrame = 0, visible = false;
    try { if (sessionStorage.getItem('aimii-comparison-hint') === 'used') comparison.hintUsed = true; } catch (_) {}
    // The hint changes only the visual split, never the selected native value.
    const restore = () => stage.style.setProperty('--comparison-position', `${range.value}%`);
    const clearHint = () => { clearTimeout(hintTimer); hintTimer = 0; cancelAnimationFrame(hintFrame); hintFrame = 0; restore(); };
    const eligible = () => visible && !comparison.hintUsed && !drag && !document.hidden && !motion.matches && !signal.aborted;
    const stopHint = () => { comparison.hintUsed = true; try { sessionStorage.setItem('aimii-comparison-hint', 'used'); } catch (_) {} clearHint(); };
    const scheduleHint = (wait = 4000) => {
      clearTimeout(hintTimer); hintTimer = 0;
      if (!eligible() || hintFrame) return;
      hintTimer = setTimeout(() => {
        hintTimer = 0;
        if (!eligible()) return;
        const start = Number(range.value), distance = Math.min(5, start, 100 - start);
        let started = null;
        const step = time => {
          if (!eligible()) { clearHint(); return; }
          if (started === null) started = time;
          const phase = Math.min(1, (time - started) / 1000);
          // Zero velocity at both ends: a quiet left/right invitation, then home.
          const offset = Math.sin(2 * Math.PI * phase) * Math.sin(Math.PI * phase) * distance;
          stage.style.setProperty('--comparison-position', `${start + offset}%`);
          if (phase < 1) hintFrame = requestAnimationFrame(step);
          else { hintFrame = 0; restore(); scheduleHint(8000); }
        };
        hintFrame = requestAnimationFrame(step);
      }, wait);
    };
    const update = value => { const position = Math.max(0, Math.min(100, Math.round(value))); stage.style.setProperty('--comparison-position', `${position}%`); range.value = String(position); range.setAttribute('aria-valuetext', `${position} % tiefgekühlt, ${100 - position} % erhitzt`); };
    const move = clientX => { const bounds = stage.getBoundingClientRect(); if (bounds.width) update((clientX - bounds.left) / bounds.width * 100); };
    listen(range, 'input', () => { stopHint(); update(Number(range.value)); }, signal);
    listen(range, 'focus', stopHint, signal);
    listen(range, 'keydown', stopHint, signal);
    listen(stage, 'pointerdown', event => { if (!event.isPrimary || event.button !== 0) return; stopHint(); drag = { id: event.pointerId, x: event.clientX, y: event.clientY, active: event.pointerType !== 'touch' }; range.focus({ preventScroll: true }); if (drag.active) { stage.setPointerCapture(event.pointerId); move(event.clientX); event.preventDefault(); } }, signal);
    listen(stage, 'pointermove', event => { if (!drag || event.pointerId !== drag.id) return; if (!drag.active) { const dx = Math.abs(event.clientX - drag.x), dy = Math.abs(event.clientY - drag.y); if (dy > 8 && dy > dx) { drag = null; return; } if (dx < 5 || dx <= dy) return; drag.active = true; stage.setPointerCapture(event.pointerId); } move(event.clientX); if (event.cancelable) event.preventDefault(); }, signal, { passive: false });
    const finish = event => { if (!drag || event.pointerId !== drag.id) return; if (event.type === 'pointerup' && !drag.active) move(event.clientX); if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId); drag = null; };
    listen(stage, 'pointerup', finish, signal); listen(stage, 'pointercancel', finish, signal);
    listen(stage, 'lostpointercapture', event => { if (drag?.id === event.pointerId) drag = null; }, signal);
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .6; if (!visible) clearHint(); scheduleHint(); }, { threshold: [0, .6] }); observer.observe(stage);
    listen(document, 'visibilitychange', () => { if (document.hidden) clearHint(); scheduleHint(); }, signal);
    listen(motion, 'change', () => { clearHint(); scheduleHint(); }, signal);
    restore();
    return () => { clearHint(); observer.disconnect(); if (drag && stage.hasPointerCapture(drag.id)) stage.releasePointerCapture(drag.id); drag = null; };
  }
  function journey(root, signal) {
    const video = root.querySelector('video'); if (!video) return;
    let visible = false, userPaused = false, automaticPause = false;
    const sync = () => { if (!visible || document.hidden || motion.matches) { if (!video.paused) { automaticPause = true; video.pause(); } return; } if (!userPaused && video.paused) video.play().catch(() => {}); };
    listen(video, 'pause', () => { if (!automaticPause) userPaused = true; automaticPause = false; }, signal);
    listen(video, 'play', () => { userPaused = false; automaticPause = false; }, signal);
    listen(document, 'visibilitychange', sync, signal); listen(motion, 'change', sync, signal);
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .35; sync(); }, { threshold: [0,.35] }); observer.observe(video);
    return () => { observer.disconnect(); video.pause(); };
  }
  function stores(root, signal) {
    const cards = [...root.querySelectorAll('[data-store]')], count = root.querySelector('[data-store-count]');
    listen(root.querySelector('[data-store-search]'), 'input', event => { const query = event.target.value.trim().toLocaleLowerCase('de'); let found = 0; cards.forEach(card => { card.hidden = !card.dataset.storeSearchText.toLocaleLowerCase('de').includes(query); if (!card.hidden) found++; }); count.textContent = `${found} Filialen`; root.querySelector('[data-store-empty]').hidden = found !== 0; }, signal);
  }
  function footerCat(root, signal) {
    let loaded = document.readyState === 'complete', near = false, started = false;
    let timer = 0, idle = null, handle = null;
    const cancel = () => {
      clearTimeout(timer); timer = 0;
      if (idle !== null) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
        else clearTimeout(idle);
        idle = null;
      }
    };
    const eligible = () => loaded && near && !document.hidden && !motion.matches && root.isConnected && !signal.aborted;
    const schedule = () => {
      cancel();
      if (started || !eligible()) return;
      timer = setTimeout(() => {
        timer = 0;
        if (!eligible()) return;
        const run = () => {
          idle = null;
          if (!eligible()) return;
          started = true;
          // The versioned module and sprite are absent from initial page requests.
          import(root.dataset.catModule).then(module => {
            if (eligible()) handle = module.mount(root);
            else started = false;
          }).catch(() => { started = false; });
        };
        idle = window.requestIdleCallback && window.cancelIdleCallback
          ? window.requestIdleCallback(run, {timeout: 2000}) : setTimeout(run, 200);
      }, 500);
    };
    const observer = new IntersectionObserver(entries => {
      near = entries.some(entry => entry.isIntersecting); schedule();
    }, {rootMargin: '200px 0px', threshold: 0});
    observer.observe(root);
    if (!loaded) listen(window, 'load', () => { loaded = true; schedule(); }, signal, {once: true});
    listen(document, 'visibilitychange', schedule, signal);
    listen(motion, 'change', schedule, signal);
    return () => { cancel(); observer.disconnect(); handle?.destroy(); };
  }
  function navigation(root, signal) {
    const dialog = root.querySelector('dialog'); if (!dialog) return;
    const opener = root.querySelector('[data-menu-open]');
    listen(opener, 'click', () => { dialog.showModal(); opener.setAttribute('aria-expanded', 'true'); }, signal);
    listen(dialog, 'click', event => { if (event.target === dialog || event.target.closest('[data-menu-close]')) dialog.close(); }, signal);
    listen(dialog, 'close', () => { opener.setAttribute('aria-expanded', 'false'); opener.focus(); }, signal);
    return () => { if (dialog.open) dialog.close(); };
  }
  function init(scope = document) {
    mount('[data-aimii-categories]', categories, scope); mount('[data-aimii-accordion]', accordion, scope); mount('[data-aimii-reviews]', reviews, scope); mount('[data-aimii-comparison]', comparison, scope); mount('[data-aimii-journey]', journey, scope); mount('[data-aimii-stores]', stores, scope); mount('[data-aimii-header]', navigation, scope); mount('[data-aimii-footer-cat]', footerCat, scope);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { [...instances].forEach(([root, destroy]) => { if (root === event.target || event.target.contains(root)) destroy(); }); });
  document.addEventListener('shopify:section:reorder', () => init());
  document.addEventListener('shopify:block:select', event => { const category = event.target.closest('[data-category-link]'); if (category) { category.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
})();
